import { useRelevantQueryParams } from '@frontend/common';
import { Functionality } from '@frontend/functionality/types';
import { OpenModalFunction, useModals } from '@frontend/modal';
import { ReportClient } from '@frontend/report/api';
import { Report, ReportType } from '@frontend/report/types';
import { useAccountRepository, useLicenseRepository } from '@frontend/repository';
import { TransactionState, TransactionStateName } from '@frontend/transaction/types';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../../redux/store';
import { useIsMount } from '../../../utils/hooks';

interface ViewProps {
    openModal: OpenModalFunction;
    currentAccount: string | null;
    navigate: ReturnType<typeof useNavigate>;
    location: ReturnType<typeof useLocation>;
    trackingNumberValid: boolean;
    setTrackingNumberValid: (valid: boolean) => void;
    activeTab: string;
    setActiveTab: (tab: string) => void;
    dispatch: ReturnType<typeof useAppDispatch>;
    report: Report | null;
    reportModalId: string;
    exportData: { [key: string]: any };
    formId: string;
    functionality: Functionality | null;
    functionalityWizardId: string | null;
}

export const useNivalizTransactions = (): ViewProps => {
    const { openModal } = useModals();
    const { currentAccount } = useAccountRepository();
    const { functionalities } = useLicenseRepository();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { functionalityId?: string } | null;
    const [trackingNumberValid, setTrackingNumberValid] = useState<boolean>(true);
    const formId = 'nivaliz-transaction-form';
    // Must match TabFilter defaultActive ('all') for initial page loads
    const [activeTab, setActiveTab] = useState<string>('all');
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMount = useIsMount();

    const [report, setReport] = useState<Report | null>(null);
    const reportModalId = 'nivaliz-transactions-report-modal';
    const data: { [key: string]: any } = { ...useRelevantQueryParams(['tab']) };
    const functionalityId = locationState?.functionalityId ?? searchParams.get('functionalityId');
    const functionality = useMemo(() => {
        if (!functionalityId) return null;
        return functionalities.find((f) => f.id === functionalityId) ?? null;
    }, [functionalities, functionalityId]);

    useEffect(() => {
        ReportClient.fetchReports({ type: ReportType.PUDO_TRANSACTIONS, account_id: currentAccount }).then((res) => {
            if (res.results.length > 0) {
                setReport(res.results[0]);
            }
        });
    }, [currentAccount]);

    const functionalityWizardId = functionality?.wizard_id ?? null;

    useEffect(() => {
        if (isMount) {
            return;
        } else {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('tab', activeTab);
                    return params;
                },
                { state: locationState ?? undefined }
            );
        }
    }, [activeTab]);

    const exportData = useMemo(() => {
        if (activeTab === 'all') {
            data['state'] = TransactionState.ALL_EXCEPT_DRAFT.map((state) => state.value);
        } else if (activeTab === 'scheduled') {
            data['state'] = [TransactionStateName.TRANSACTION_SCHEDULED];
        } else if (activeTab === 'active') {
            // Active = awaiting drop-off + awaiting pick-up + processing
            data['state'] = [
                TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
                TransactionStateName.TRANSACTION_PICK_UP_PENDING,
                ...(TransactionState.NOT_COMPLETED_CUSTOMER || []).map((state) => state.value)
            ];
        } else if (activeTab === 'completed') {
            data['state'] = [TransactionStateName.TRANSACTION_COMPLETED, TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES];
        } else if (activeTab === 'failed') {
            data['state'] = [TransactionStateName.TRANSACTION_FAILED];
        }
        return data;
    }, [searchParams, activeTab]);

    return {
        openModal,
        currentAccount,
        navigate,
        location,
        trackingNumberValid,
        setTrackingNumberValid,
        activeTab,
        setActiveTab,
        dispatch,
        report,
        reportModalId,
        exportData,
        formId,
        functionality,
        functionalityWizardId
    };
};
